import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import getLead from "../http/getLead";
import getLeadContactEmails from "../http/getLeadContactEmails";
import getLeads from "../http/getLeads";
import postLeadOutreachDraft, {
  OutreachDraftRequest,
} from "../http/postLeadOutreachDraft";
import { getVoaRatingListStatus } from "../http/getVoaStatus";
import patchLead, { UpdateLeadRequest } from "../http/patchLead";
import patchLeadDecisions, {
  UpdateLeadDecisionsRequest,
} from "../http/patchLeadDecisions";
import patchVoaReview, { VoaReviewRequest } from "../http/patchVoaReview";
import getOperations, {
  deleteOperation as deleteOperationHttp,
  getOperationByKey,
  postCancelOperation,
} from "../http/getOperations";
import postLeadPremiumEnrich from "../http/postLeadPremiumEnrich";
import postLeadRefetch from "../http/postLeadRefetch";
import postLeadsBulkPremiumEnrich, {
  LeadsBulkPremiumEnrichRequest,
} from "../http/postLeadsBulkPremiumEnrich";
import postVoaRefetch from "../http/postVoaRefetch";
import postVoaRefetchDegraded from "../http/postVoaRefetchDegraded";
import postVoaReject from "../http/postVoaReject";
import postVoaPromote, { VoaPromoteRequest } from "../http/postVoaPromote";
import postVoaPremiumEnrich, {
  VoaPremiumEnrichRequest,
} from "../http/postVoaPremiumEnrich";
import postLeadBulkTags, { BulkTagsRequest } from "../http/postLeadBulkTags";
import postLeadBulkDelete, {
  BulkDeleteRequest,
} from "../http/postLeadBulkDelete";
import {
  postBulkCreateSites,
  postBulkCreateSitesPreview,
} from "../http/postBulkCreateSites";
import { deleteLead } from "../http/deleteLead";
import {
  CsvImportRequest,
  postLeadImportCsv,
} from "../http/postLeadImport";
import postLeadSearch, { LeadSearchRequest } from "../http/postLeadSearch";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

const useLeads = () => {
  const queryClient = useQueryClient();
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);

  const fetchLeads = useQuery(
    ["leads"],
    async () => getLeads(await getAuthTokenBlocking(getToken, relogin)),
    { enabled: true }
  );

  const searchLeadCandidates = useMutation(async (body: LeadSearchRequest) =>
    postLeadSearch(await getAuthTokenBlocking(getToken, relogin), body)
  );

  const updateLead = useMutation(
    async ({ id, ...body }: UpdateLeadRequest & { id: string }) =>
      patchLead(await getAuthTokenBlocking(getToken, relogin), id, body),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead", data.lead.id]);
      },
    }
  );

  const updateLeadDecisions = useMutation(
    async ({
      id,
      ...body
    }: UpdateLeadDecisionsRequest & { id: string }) =>
      patchLeadDecisions(
        await getAuthTokenBlocking(getToken, relogin),
        id,
        body
      ),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead", data.lead.id]);
      },
    }
  );

  const reviewVoaCandidate = useMutation(
    async ({
      sourceRef,
      ...body
    }: VoaReviewRequest & { sourceRef: string }) =>
      patchVoaReview(
        await getAuthTokenBlocking(getToken, relogin),
        sourceRef,
        body
      )
  );

  const rejectVoaCandidate = useMutation(async (sourceRef: string) =>
    postVoaReject(await getAuthTokenBlocking(getToken, relogin), sourceRef)
  );

  const refetchVoaCandidate = useMutation(async (sourceRef: string) =>
    postVoaRefetch(await getAuthTokenBlocking(getToken, relogin), sourceRef)
  );

  // Bulk variant: re-runs only the upstream sources that were
  // degraded last time the row was enriched (driven by each row's
  // persisted `enrichmentDegradedSources`). Cheaper than
  // refetchVoaCandidate when only one upstream had a transient
  // outage - we don't bust the buildings/HMLR/EPC caches.
  const refetchVoaDegradedSources = useMutation(
    async (sourceRefs: string[]) =>
      postVoaRefetchDegraded(
        await getAuthTokenBlocking(getToken, relogin),
        sourceRefs
      )
  );

  const premiumEnrichVoaCandidates = useMutation(
    async (body: VoaPremiumEnrichRequest) =>
      postVoaPremiumEnrich(
        await getAuthTokenBlocking(getToken, relogin),
        body
      )
  );

  const refetchLead = useMutation(
    async (leadId: string) =>
      postLeadRefetch(await getAuthTokenBlocking(getToken, relogin), leadId),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead", data.lead.id]);
      },
    }
  );

  const premiumEnrichLead = useMutation(
    async (leadId: string) =>
      postLeadPremiumEnrich(
        await getAuthTokenBlocking(getToken, relogin),
        leadId
      ),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["lead", data.lead.id]);
      },
    }
  );

  // Kick off the bulk premium-enrich as a long-running operation.
  // Returns {operationKey, siteId, leadCount} immediately; the actual
  // enrichment runs in the long-running-operation Lambda. The FE polls
  // `useOperation(operationKey, siteId)` until status is terminal,
  // then invalidates the leads cache so the refreshed rows show up.
  const bulkPremiumEnrichLeads = useMutation(
    async (body: LeadsBulkPremiumEnrichRequest) =>
      postLeadsBulkPremiumEnrich(
        await getAuthTokenBlocking(getToken, relogin),
        body
      ),
    {
      onSuccess: () => {
        // Mutation only kicks off the LRO; the real invalidation
        // happens when the polling hook sees the terminal "completed"
        // status. Harmless insurance against any other surface
        // showing a partly-stale leads list.
        queryClient.invalidateQueries(["leads"]);
      },
    }
  );

  const promoteVoaCandidates = useMutation(
    async (body: VoaPromoteRequest) =>
      postVoaPromote(await getAuthTokenBlocking(getToken, relogin), body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
      },
    }
  );

  const bulkUpdateLeadTags = useMutation(
    async (body: BulkTagsRequest) =>
      postLeadBulkTags(await getAuthTokenBlocking(getToken, relogin), body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
      },
    }
  );

  const bulkDeleteLeads = useMutation(
    async (body: BulkDeleteRequest) =>
      postLeadBulkDelete(await getAuthTokenBlocking(getToken, relogin), body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
      },
    }
  );

  // Plan: returns the preview shape (sitesToCreate, skipped,
  // orgsToReuse, orgsToCreate). The FE renders this in a confirm
  // modal; no writes yet.
  const previewBulkCreateSites = useMutation(async (leadIds: string[]) =>
    postBulkCreateSitesPreview(
      await getAuthTokenBlocking(getToken, relogin),
      leadIds
    )
  );

  // Kick off the bulk-create as a long-running operation. Returns
  // {operationKey, siteId} immediately; the actual create work
  // happens in the long-running-operation Lambda. The FE polls
  // `useOperation(operationKey, siteId)` until status === "completed"
  // and reads the `result` for the created sites/orgs/skipped lists.
  const bulkCreateSites = useMutation(
    async (leadIds: string[]) =>
      postBulkCreateSites(await getAuthTokenBlocking(getToken, relogin), leadIds),
    {
      onSuccess: () => {
        // Created sites land in the Sites list + new orgs land in
        // the Organisations list; invalidate both so the admin app's
        // other tabs reflect the bulk action.
        queryClient.invalidateQueries(["leads"]);
        queryClient.invalidateQueries(["sites"]);
        queryClient.invalidateQueries(["organisations"]);
      },
    }
  );

  // Kick off the CSV/XLSX import as a long-running operation. Returns
  // {operationKey, siteId, rowCount} immediately; the per-row
  // enrichment + persistence happens in the long-running-operation
  // Lambda. The FE polls `useOperation(operationKey, siteId)` until
  // status === "completed" and reads the `result` for the created
  // leads + per-row errors. Mirrors `bulkCreateSites`.
  const importLeadsFromCsv = useMutation(
    async (body: CsvImportRequest) =>
      postLeadImportCsv(await getAuthTokenBlocking(getToken, relogin), body),
    {
      onSuccess: () => {
        // The operation only just kicked off; the real invalidation
        // happens when the polling hook sees the terminal "completed"
        // status. This invalidate is harmless insurance.
        queryClient.invalidateQueries(["leads"]);
      },
    }
  );

  const cancelOperation = useMutation(async (operationKey: string) =>
    postCancelOperation(
      await getAuthTokenBlocking(getToken, relogin),
      operationKey
    )
  );

  const deleteOperation = useMutation(async (operationKey: string) =>
    deleteOperationHttp(
      await getAuthTokenBlocking(getToken, relogin),
      operationKey
    )
  );

  const deleteLeadMutation = useMutation(
    async (leadId: string) =>
      deleteLead(await getAuthTokenBlocking(getToken, relogin), leadId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["leads"]);
      },
    }
  );

  return {
    fetchLeads,
    searchLeadCandidates,
    updateLead,
    updateLeadDecisions,
    reviewVoaCandidate,
    rejectVoaCandidate,
    refetchVoaCandidate,
    refetchVoaDegradedSources,
    premiumEnrichVoaCandidates,
    refetchLead,
    premiumEnrichLead,
    bulkPremiumEnrichLeads,
    promoteVoaCandidates,
    bulkUpdateLeadTags,
    bulkDeleteLeads,
    previewBulkCreateSites,
    bulkCreateSites,
    importLeadsFromCsv,
    deleteLead: deleteLeadMutation,
    cancelOperation,
    deleteOperation,
  };
};

export const useLeadSearchOperations = (opts?: {
  enabled?: boolean;
  refetchInterval?: number | false;
}) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["leadSearchOperations"],
    async () =>
      getOperations(await getAuthTokenBlocking(getToken, relogin)),
    {
      enabled: opts?.enabled ?? true,
      // Default 5s polling matches the design doc's status-page
      // cadence; callers can override (false to disable, or a
      // longer interval when nothing is in flight).
      refetchInterval: opts?.refetchInterval ?? 5000,
    }
  );
};

// Single-operation polling hook. Used by surfaces that have just
// kicked off a long-running operation and want to watch *that one*
// row finish (rather than re-fetching the whole list every tick).
// Stops polling once the operation reaches a terminal status. Pass
// `null` for either id to disable.
export const useOperation = (
  operationKey: string | null,
  siteId: string | null,
  opts?: { refetchInterval?: number | false }
) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  return useQuery(
    ["operation", operationKey, siteId],
    async () =>
      getOperationByKey(
        await getAuthTokenBlocking(getToken, relogin),
        operationKey!,
        siteId!
      ),
    {
      enabled: !!operationKey && !!siteId,
      refetchInterval: (data) => {
        if (data?.operation?.status &&
            ["completed", "failed", "cancelled"].includes(data.operation.status)) {
          return false;
        }
        return opts?.refetchInterval ?? 2000;
      },
    }
  );
};

export const useLead = (leadId: string | undefined) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);

  return useQuery(
    ["lead", leadId],
    async () =>
      getLead(await getAuthTokenBlocking(getToken, relogin), leadId!),
    { enabled: !!leadId }
  );
};

// Tenant + landlord contact emails for a lead, auto-pulled from the
// configured sources (Gmail / HubSpot / Apollo). Only enabled when the
// caller passes a leadId (the section is shown for leads with a site).
export const useLeadContactEmails = (leadId: string | undefined) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);

  return useQuery(
    ["leadContactEmails", leadId],
    async () =>
      getLeadContactEmails(await getAuthTokenBlocking(getToken, relogin), leadId!),
    { enabled: !!leadId }
  );
};

// Generate a short outreach-email draft for a lead's tenant/landlord
// contact. On success the draft is placed in the logged-in user's Gmail.
export const useLeadOutreachDraft = (leadId: string | undefined) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);

  return useMutation(async (body: OutreachDraftRequest) =>
    postLeadOutreachDraft(await getAuthTokenBlocking(getToken, relogin), leadId!, body)
  );
};

export const useVoaRatingListStatus = () => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);

  return useQuery(["voaStatus"], async () =>
    getVoaRatingListStatus(await getAuthTokenBlocking(getToken, relogin))
  );
};

export default useLeads;
